import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { Collections } from '@graylog/enterprise-api';

import type { CollectionListItem, SortableCollectionAttributes } from 'collections/types';
import type { Attribute, SearchParams } from 'stores/PaginationTypes';
import type { PaginatedResponse } from 'components/common/PaginatedEntityTable/useFetchEntities';
import { defaultOnError } from 'util/conditional/onError';

const INITIAL_DATA = {
  pagination: { total: 0 },
  list: [],
  attributes: [],
};

export const COLLECTIONS_QUERY_KEY = 'collections';

export const keyFn = (searchParams: SearchParams) => [COLLECTIONS_QUERY_KEY, searchParams];

export type PaginatedCollectionResponse = {
  list: Readonly<Array<CollectionListItem>>;
  pagination: { total: number };
  attributes: Array<Attribute>;
};
export const fetchPaginatedCollections = async (
  searchParams: SearchParams,
  editable: boolean = false,
): Promise<PaginatedResponse<CollectionListItem>> =>
  Collections.getPage(
    searchParams.sort.attributeId as SortableCollectionAttributes,
    searchParams.page,
    searchParams.pageSize,
    searchParams.query,
    searchParams.sort.direction,
    editable,
  ).then(({ elements, query, attributes, pagination: { count, total, page, per_page: perPage } }) => ({
    list: elements,
    attributes: attributes,
    pagination: {
      count,
      total,
      page,
      perPage,
      query,
    },
  }));

const useCollections = (
  searchParams: SearchParams,
  { enabled } = { enabled: true },
): {
  data: PaginatedCollectionResponse;
  isLoading: boolean;
  refetch: () => void;
} => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: keyFn(searchParams),
    queryFn: () =>
      defaultOnError(
        fetchPaginatedCollections(searchParams),
        'Loading Collections failed with status',
        'Could not load Collections',
      ),
    placeholderData: keepPreviousData,
    enabled,
  });

  return {
    data: data ?? INITIAL_DATA,
    isLoading,
    refetch,
  };
};

export default useCollections;
